import { useEffect, useRef, useState } from "react";

// One worker per setup so the three checks run concurrently rather than
// queueing behind each other on a single worker thread.
const SETUP_KEYS = ["inclined", "coplanar", "gimbal"];

/**
 * Debounced, off-main-thread "is the volume within FOV" check for all three
 * setups. Returns { inclined, coplanar, gimbal } booleans (default true,
 * i.e. assumed fine, until the first result arrives). Stale in-flight
 * requests are discarded by requestId so rapid slider input never queues up
 * work or lets an old result overwrite a newer one out of order.
 */
export function useWithinFov({
  vecsByKey,
  halfU,
  halfV,
  volRes,
  volX,
  volY,
  volZ,
  offsetVolX,
  offsetVolY,
  offsetVolZ,
  enabled,
}) {
  const [result, setResult] = useState({ inclined: true, coplanar: true, gimbal: true });
  const workersRef = useRef({});
  const requestIdRef = useRef({ inclined: 0, coplanar: 0, gimbal: 0 });

  useEffect(() => {
    const workers = {};
    for (const key of SETUP_KEYS) {
      const worker = new Worker(new URL("./withinFovWorker.js", import.meta.url));
      worker.onmessage = (e) => {
        const { requestId, result: r } = e.data;
        if (requestId !== requestIdRef.current[key]) return; // stale, discard
        setResult((prev) => ({ ...prev, [key]: r }));
      };
      workers[key] = worker;
    }
    workersRef.current = workers;
    return () => {
      for (const key of SETUP_KEYS) workers[key].terminate();
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setResult({ inclined: true, coplanar: true, gimbal: true });
      return;
    }
    const id = setTimeout(() => {
      for (const key of SETUP_KEYS) {
        const worker = workersRef.current[key];
        if (!worker) continue;
        const requestId = ++requestIdRef.current[key];
        worker.postMessage({
          requestId,
          vecs: vecsByKey[key],
          halfU,
          halfV,
          volRes,
          volX,
          volY,
          volZ,
          offsetVolX,
          offsetVolY,
          offsetVolZ,
        });
      }
    }, 200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vecsByKey,
    halfU,
    halfV,
    volRes,
    volX,
    volY,
    volZ,
    offsetVolX,
    offsetVolY,
    offsetVolZ,
    enabled,
  ]);

  return result;
}
